import { EmojiEvents, Logout, PhotoCamera, SportsBar } from '@mui/icons-material';
import { Avatar, Box, Button, Card, CardContent, Chip, Divider, IconButton, Typography } from '@mui/material';
import axios from 'axios';
import React, { Component } from 'react';
import PermissionCheck from './Elements/PermissionCheck';
import ProfileImageSelector from './Elements/ProfileImageSelector';
import { setAuthToken } from './Hooks/setAuthToken';
import history from './Routes/history';
import { Award, User } from './Types/Types';
interface Props {
    user: User | null,
    awards: Award[],
};

const PROFILE_IMAGES = [
    'hoff-1.jpg',
    'hoff-2.jpg',
    'hoff-3.jpg',
    'hoff-4.jpg',
    'hoff-5.jpg',
];

class Profile extends Component<Props, { loading : boolean, permissions: PermissionName[], imageModalOpen: boolean }> {
    constructor(props : Props) {
        super(props);
        
        this.state={
            loading: false,
            permissions: [],
            imageModalOpen: false,
        };
    }

    componentDidMount() {
        const { permissions } = this.state;
        let newPermissions = [...permissions];

        const wanted : PermissionName[] = [
            "geolocation",
        ];
        const self = this;
        
        // Check if Permissions API is supported (not available in all browsers/contexts)
        if (navigator.permissions && typeof navigator.permissions.query === 'function') {
            wanted.forEach((perm : PermissionName) => {
                navigator.permissions.query({name: perm})
                    .then(function(result) {
                        if (result.state === 'granted') {
                            newPermissions.push(perm);
                            self.setState({
                                permissions: newPermissions,
                            });
                        } else if (result.state === 'prompt') {}
                    })
                    .catch(function(error) {
                        // Permission query not supported for this permission type
                        // This is expected in some browsers (e.g., Safari)
                        console.log('Permission query not supported:', error);
                    }); 
            });
        } else {
            // Fallback: If Permissions API is not supported, we can't check permission state
            // The user will be prompted when geolocation is actually used
            console.log('Permissions API not supported in this browser');
        }
    }
    

    getEarnedAwards = () => {
        const { user, awards } = this.props;

        if (user) {
            const earned = awards.filter(award => {
                return user.awards.includes(award.id);
            });
            return earned;
        }
        return [];
    }

    getProfileImages = () => {
        let images : string[] = [
            ...PROFILE_IMAGES
        ];
        const earned = this.getEarnedAwards();
        earned.forEach(award => {
            images.push(award.image);
        });
        return images;
    }

    handleLogout = (e : React.MouseEvent): void => {
        setAuthToken(null);
        setTimeout(() => {
            history.push('/login');
        }, 200);
    }

    handleOpenImageModal = (): void => {
        this.setState({ imageModalOpen: true });
    }

    handleCloseImageModal = (): void => {
        this.setState({ imageModalOpen: false });
    }

    handleUpdateImage = (image : string): void => {
        this.setState({
            loading: true,
        }, () => {
            axios.post('/user/image', {
                image: image
            })
            .then(() => {
                this.setState({
                    loading: false,
                });
                document.dispatchEvent(new CustomEvent('update'));
            });
        });
    }

    handleRequestGeolocationPermission = (e: React.MouseEvent): void => {
        navigator.geolocation.getCurrentPosition((position => {
            console.log('Geolocation permission granted:', position);
            this.setState(prevState => ({
                permissions: [...prevState.permissions, 'geolocation']
            }));
        }), error => {
            console.log('Geolocation permission denied or error occurred:', error);
        });
    }

    render() {
        const { loading, permissions, imageModalOpen } = this.state;
        const { user } = this.props;

        const getAvatar = (imageParam : null | string = null): string | undefined => {
            if (user) {
                let image = imageParam ?? user.image;
                if (image.startsWith('hoff')) {
                    return process.env.PUBLIC_URL + '/img/' + image;
                }
                return process.env.PUBLIC_URL + '/awards/' + image;
            }
            return undefined;
        }

        const getName = (): String | undefined => {
            if (user) {
                return user.username;
            }
            return undefined;
        }

        const getAwardAvatar = (award : Award): string => {
            return process.env.PUBLIC_URL + '/awards/' + award.image;
        }

        const beerCount = user?.total_beers ?? user?.awards?.length ?? 0;

        return (
            <Box sx={{ flexGrow: 1}}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: '100%',
                    backgroundColor: '#232323'
                }}>
                    {/* Profile Information Card */}
                    <Box sx={{ p: 3, pt: 4 }}>
                        <Card sx={{ 
                            backgroundColor: '#313131',
                            border: '1px solid rgba(255, 215, 0, 0.18)',
                            borderRadius: '24px',
                            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
                            overflow: 'visible'
                        }}>
                            <CardContent sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center',
                                p: 3,
                                '&:last-child': { pb: 3 }
                            }}>
                                {/* Profile Picture with Camera Icon */}
                                <Box sx={{ position: 'relative', mb: 2 }}>
                                    <Avatar 
                                        alt={String(user?.username) || "Profile"}
                                        src={getAvatar()} 
                                        sx={{ 
                                            height: 128, 
                                            width: 128,
                                            border: '4px solid rgba(255, 215, 0, 0.3)',
                                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                                        }} 
                                    />
                                    <IconButton
                                        onClick={this.handleOpenImageModal}
                                        disabled={loading}
                                        aria-label="Change profile picture"
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            backgroundColor: '#FFD700',
                                            color: '#232323',
                                            '&:hover': {
                                                backgroundColor: '#E2B13C',
                                            },
                                            width: 40,
                                            height: 40,
                                        }}
                                    >
                                        <PhotoCamera />
                                    </IconButton>
                                </Box>

                                {/* Profile Name */}
                                <Typography 
                                    variant="h4" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        mb: 1,
                                        textAlign: 'center'
                                    }}
                                >
                                    {getName()}
                                </Typography>

                                {/* Group Badge */}
                                {user && user.group_id && (
                                    <Chip 
                                        label={user.group_shorthand} 
                                        sx={{ 
                                            fontSize: '1em',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            backgroundColor: 'rgba(255, 215, 0, 0.15)',
                                            color: '#FFD700',
                                            border: '1px solid rgba(255, 215, 0, 0.3)',
                                            borderRadius: '12px',
                                            padding: '4px 8px'
                                        }}
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Awards Section */}
                    <Box sx={{ px: 3, pb: 2 }}>
                        {/* Beer Counter Badge as Header */}
                        <Box sx={{
                            gap: 1,
                            backgroundColor: 'rgba(255, 215, 0, 0.1)',
                            borderRadius: '16px',
                            padding: '12px 24px',
                            border: '2px solid rgba(255, 215, 0, 0.3)',
                            mb: 2,
                            width: 'fit-content',
                            margin: '0 auto 16px auto'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1
                            }}>
                                <SportsBar sx={{ color: '#FFD700', fontSize: 28 }} />
                                <Typography variant="h5" sx={{ fontWeight: 600, color: '#FFD700' }}>
                                    {beerCount}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#FFD700', fontWeight: 500 }}>
                                    Beers
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1
                            }}>
                                <EmojiEvents sx={{ color: '#FFD700', fontSize: 28 }} />
                                <Typography variant="h5" sx={{ fontWeight: 600, color: '#FFD700' }}>
                                    {this.getEarnedAwards().length}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#FFD700', fontWeight: 500 }}>
                                    Ehrunga
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 1.5,
                            justifyContent: 'center',
                            backgroundColor: 'rgba(49, 49, 49, 0.5)',
                            borderRadius: '16px',
                            padding: 2,
                            minHeight: '80px'
                        }}>
                            {this.getEarnedAwards().length > 0
                                ? this.getEarnedAwards().map((award : Award, index : number) => {
                                    return (
                                        <Box 
                                            key={"award-" + index}
                                            onClick={() => this.handleUpdateImage(award.image)}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.1)',
                                                },
                                                '&:active': {
                                                    transform: 'scale(0.95)',
                                                }
                                            }}
                                        >
                                            <Avatar 
                                                src={getAwardAvatar(award)}
                                                alt={award.name}
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    border: '2px solid rgba(255, 215, 0, 0.3)',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                                                }}
                                            />
                                        </Box>
                                    );
                                })
                                : (
                                    <Box sx={{ 
                                        textAlign: 'center',
                                        width: '100%',
                                        py: 2,
                                        opacity: 0.5
                                    }}>
                                        Hosd hoid einfoch no koa!
                                    </Box>
                                )
                            }
                        </Box>
                    </Box>

                    {/* Permissions Section */}
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                mb: 2, 
                                fontWeight: 600,
                                textAlign: 'center'
                            }}
                        >
                            Wos ku a ois da Bua?
                        </Typography>
                        <Card sx={{ 
                            backgroundColor: 'rgba(49, 49, 49, 0.5)',
                            borderRadius: '16px',
                            border: '1px solid rgba(255, 215, 0, 0.1)'
                        }}>
                            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                <PermissionCheck 
                                    permissions={permissions} 
                                    permission={'geolocation'} 
                                    label={'GPS-Stondort'}
                                    requestPermissionButton={<Button onClick={(e) => this.handleRequestGeolocationPermission(e)}>Berechtigung hea du Lausa!</Button>}
                                />
                                {!permissions.includes('geolocation') && <Box sx={{ p: 2 }}>
                                    <b>Wenns Probleme gib mitn iPhone</b><br/>
                                    Unter Einstellungen &gt; Ortungsdienste measn allgemein aktiv sei.<br/>
                                    Unter Einstellungen &gt; Ortungsdienste muas bei Safari bzw. dein Browser a auf "Beim Verwenden der App" oder "Nächstes mal fragen" eingstöht sei.<br/>
                                    Unter Einstellungen &gt; Apps &gt; Safari bzw. dein Browser muas da Standort a aktiviert sei.<br/>
                                </Box>}
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Spacer */}
                    <Box sx={{ flexGrow: 1 }} />

                    {/* Logout Button */}
                    <Box sx={{ p: 3 }}>
                        <Divider sx={{ mb: 2, borderColor: 'rgba(255, 215, 0, 0.18)' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button 
                                variant="contained" 
                                startIcon={<Logout />} 
                                onClick={this.handleLogout}
                                sx={{
                                    backgroundColor: '#FFD700',
                                    color: '#232323',
                                    fontWeight: 600,
                                    borderRadius: '24px',
                                    px: 4,
                                    py: 1.5,
                                    '&:hover': {
                                        backgroundColor: '#E2B13C',
                                    }
                                }}
                            >
                                i schleich mi
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Profile Image Selector Modal */}
                <ProfileImageSelector
                    open={imageModalOpen}
                    currentImage={String(user?.image) || ''}
                    images={this.getProfileImages()}
                    onClose={this.handleCloseImageModal}
                    onSelectImage={this.handleUpdateImage}
                    getAvatarUrl={getAvatar}
                />
            </Box>
        );
    }
}
export default Profile;